import { CSSProperties, ReactNode, RefObject, useEffect, useRef } from 'react';

import { cx } from '@libs/classnames';
import { scrollToElement } from '@libs/scroll';

import styles from './HorizontalScroll.module.css';

export function HorizontalScroll({
  ref,
  style,
  className,
  enableForDesktop,
  fullWidth,
  children,
  scrollToSelector,
  scrollToSelectorBlock,
}: {
  ref?: RefObject<HTMLDivElement | null>;
  style?: CSSProperties;
  className?: string;
  enableForDesktop?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
  scrollToSelector?: string;
  scrollToSelectorBlock?: 'nearest' | 'center';
}) {
  const innerRef = useRef<HTMLDivElement>(null);
  const scrollBehavior = useRef<'instant' | 'smooth'>('instant');

  useEffect(() => {
    const behavior = scrollBehavior.current;

    scrollBehavior.current = 'smooth';

    if (!scrollToSelector || !innerRef?.current) return;

    const selectedEl = innerRef.current.querySelector(scrollToSelector) as HTMLElement;

    if (!selectedEl) return;

    scrollToElement({
      container: innerRef.current,
      element: selectedEl,
      direction: 'horizontal',
      behavior,
      block: scrollToSelectorBlock,
    });
  }, [ref, scrollToSelector, scrollToSelectorBlock]);

  return (
    <div
      ref={(el) => {
        if (ref) ref.current = el;
        innerRef.current = el;
      }}
      style={style}
      className={cx(className, styles.scrollable, {
        [styles.enabledForDesktop]: enableForDesktop,
        [styles.is_full_width]: fullWidth,
      })}
    >
      {children}
    </div>
  );
}
